import { useEffect, useRef, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import CommonTable from "../component/CommonTable";
import MessageToster from "../component/MessageToster";
import axios from "axios";
import { FileSaver } from "../component/FileSaver";
import Loader from "../component/Loader";
import { BASE_URL } from "../api";
import { closeModal } from "../utils/modal";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders, getFullMediaUrl } from "../utils/helpers";

const getTodayDateString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getTomorrowDateString = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yyyy = tomorrow.getFullYear();
  const mm = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const dd = String(tomorrow.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const isPdfUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  return url.toLowerCase().split("?")[0].endsWith(".pdf");
};

export default function KycDocuments() {
  const fileUpload = useRef(null);
  const frontFileRef = useRef(null);
  const backFileRef = useRef(null);

  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);

  const [selectedItem, setSelectedItem] = useState({
    document_front_image: null,
    document_back_image: null
  });

  const tableRef = useRef()
  const [docdata, setDocdata] = useState([])
  const [kycData, setKycData] = useState([])
  const [input, setInput] = useState({
    // title: "",
    doctype: "",
    document_front_image: null,
    document_back_image: null,
    expiry_date: ""
  }
  )
  const [validations, setValidations] = useState({
    // title: "",
    doctype: "",
    document_front_image: "",
    document_back_image: "",
    expiry_date: ""
  });

  const [preview, setPreview] = useState({
    front: null,
    back: null
  });

  const [kycStatus, setKycStatus] = useState(null);

  const validationRules = {
    // title: "Title is required.",
    doctype: "Document type is required.",
    document_front_image: "Document front image is required.",
    document_back_image: "Document back image is required.",
    expiry_date: "Expiry date is required."
  };

  const handleReset = () => {
    if (preview.front) URL.revokeObjectURL(preview.front);
    if (preview.back) URL.revokeObjectURL(preview.back);

    setInput({
      // title: "",
      doctype: "",
      document_front_image: null,
      document_back_image: null,
      expiry_date: ""
    });

    setValidations({
      // title: "",
      doctype: "",
      document_front_image: "",
      document_back_image: "",
      expiry_date: ""
    });

    setPreview({
      front: null,
      back: null
    });

    // if (fileUpload.current) {
    //   fileUpload.current.value = "";
    // }
    if (frontFileRef.current) frontFileRef.current.value = "";
    if (backFileRef.current) backFileRef.current.value = "";
  };

  const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf"
  ];
  const ALLOWED_EXT_REGEX = /\.(jpe?g|png|pdf)$/i;

  const isAllowedFile = (file) => {
    if (!file) return false;
    const hasValidType = ALLOWED_FILE_TYPES.includes(file.type);
    const hasValidExt = ALLOWED_EXT_REGEX.test(file.name || "");
    return hasValidType || hasValidExt;
  };

  const isPdfFile = (file) => {
    if (!file) return false;
    return file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
  };

  const handleRemoveFile = (fieldName) => {
    const previewKey = fieldName === "document_front_image" ? "front" : "back";

    if (preview[previewKey]) {
      URL.revokeObjectURL(preview[previewKey]);
    }

    setPreview((prev) => ({
      ...prev,
      [previewKey]: null
    }));

    setInput((prev) => ({
      ...prev,
      [fieldName]: null
    }));

    if (fieldName === "document_front_image" && frontFileRef.current) {
      frontFileRef.current.value = "";
    }
    if (fieldName === "document_back_image" && backFileRef.current) {
      backFileRef.current.value = "";
    }

    setValidations((prev) => ({
      ...prev,
      [fieldName]: ""
    }));
  };

  const handelchange = (e) => {
    const { name, value, files, type } = e.target;

    if (type === "file" && files.length > 0) {
      setInput((prev) => ({
        ...prev,
        [name]: files[0]
      }));
      setValidations((prev) => ({
        ...prev,
        [name]: ""
      }));
    } else {
      setInput((prev) => ({
        ...prev,
        [name]: value
      }));

      if (name === "expiry_date") {
        const todayStr = getTodayDateString();
        if (value && value <= todayStr) {
          setValidations((prev) => ({
            ...prev,
            expiry_date: "Expiry date must be a future date."
          }));
        } else {
          setValidations((prev) => ({
            ...prev,
            expiry_date: ""
          }));
        }
      } else {
        setValidations((prev) => ({
          ...prev,
          [name]: ""
        }));
      }
    }
  };

  const handleImagePreview = (e) => {
    const { name, files } = e.target;
    const file = files && files[0];
    const previewKey = name === "document_front_image" ? "front" : "back";

    if (!file) {
      if (preview[previewKey]) URL.revokeObjectURL(preview[previewKey]);
      setPreview((prev) => ({ ...prev, [previewKey]: null }));
      setInput((prev) => ({ ...prev, [name]: null }));
      return;
    }

    if (!isAllowedFile(file)) {
      MessageToster("error", "Only JPG, JPEG, PNG, and PDF files are allowed.");
      e.target.value = "";
      if (preview[previewKey]) URL.revokeObjectURL(preview[previewKey]);
      setPreview((prev) => ({ ...prev, [previewKey]: null }));
      setInput((prev) => ({ ...prev, [name]: null }));
      setValidations((prev) => ({
        ...prev,
        [name]: "Only JPG, JPEG, PNG, and PDF files are allowed."
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      MessageToster("error", "File size must not exceed 5MB.");
      e.target.value = "";
      if (preview[previewKey]) URL.revokeObjectURL(preview[previewKey]);
      setPreview((prev) => ({ ...prev, [previewKey]: null }));
      setInput((prev) => ({ ...prev, [name]: null }));
      setValidations((prev) => ({
        ...prev,
        [name]: "File size must not exceed 5MB."
      }));
      return;
    }

    if (preview[previewKey]) URL.revokeObjectURL(preview[previewKey]);
    const fileURL = URL.createObjectURL(file);

    setPreview((prev) => ({
      ...prev,
      [previewKey]: fileURL
    }));

    setInput((prev) => ({
      ...prev,
      [name]: file
    }));

    setValidations((prev) => ({
      ...prev,
      [name]: ""
    }));
  };

  const columns = [
    // { header: 'Title', accessor: 'title' },
    { header: 'Type', accessor: 'doc_type' },
    { header: 'Created Date', accessor: 'submitted_at' },
    { header: 'Expiry Date', accessor: 'expiry_date' },
    {
      header: 'Status',
      render: (row) => row.document_front_image ? (
        <button
          type="button"
          className={`btn waves-effect waves-light ${row.status === "Approve" ? "btn-success"
            : row.status === "In Process" ? "btn-warning"
              : row.status === "Reject" ? "btn-danger"
                : "btn-secondary"
            }`}
        >
          {row.status}
        </button>
      ) : null
    },
    {
      header: 'Action',
      render: (row) => row.document_front_image ? (
        <ul className="list-unstyled hstack gap-1 mb-0">
          <li title="View">
            <button
              type="button"
              className="btn btn-sm btn-soft-primary"
              data-bs-toggle="modal"
              data-bs-target="#ibAccountModal"
              onClick={() => setSelectedItem({
                front: getFullMediaUrl(row.document_front_image),
                back: row.document_back_image ? getFullMediaUrl(row.document_back_image) : null,
              })}
            >
              <i className="mdi mdi-eye-outline fs-5" />
            </button>
          </li>
          <li title="Download">
            <button
              className="btn btn-sm btn-soft-success"
              onClick={() => FileSaver(getFullMediaUrl(row.document_front_image))}
            >
              <i className="mdi mdi-download-outline fs-5" />
            </button>
          </li>
        </ul>
      ) : null
    }
  ];

  useEffect(() => {
    // displayButtons(tableRef) removed
  }, []);

  const onSubmitForm = async () => {
    let errors = {};

    for (const field in input) {
      if (!input[field]) {
        errors[field] = validationRules[field];
      }
    }

    if (input.expiry_date) {
      const todayStr = getTodayDateString();
      if (input.expiry_date <= todayStr) {
        errors.expiry_date = "Expiry date must be a future date.";
      }
    }

    if (input.document_front_image) {
      if (!isAllowedFile(input.document_front_image)) {
        errors.document_front_image = "Only JPG, JPEG, PNG, and PDF files are allowed.";
      } else if (input.document_front_image.size > 5 * 1024 * 1024) {
        errors.document_front_image = "Front document must be less than 5MB.";
      }
    }

    if (input.document_back_image) {
      if (!isAllowedFile(input.document_back_image)) {
        errors.document_back_image = "Only JPG, JPEG, PNG, and PDF files are allowed.";
      } else if (input.document_back_image.size > 5 * 1024 * 1024) {
        errors.document_back_image = "Back document must be less than 5MB.";
      }
    }

    setValidations(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const uploadSuccess = await uploadKycDetails();
    if (uploadSuccess) {
      await checkKycStatus();
    }
    // await uploadKycDetails();
    // await checkKycStatus();
  };

  const uploadKycDetails = async () => {
    const mid = sessionStorage.getItem('mid');

    const inputForm = {
      mid: Number(mid),
      title: "",
      doctype: input.doctype,
      document_front_image: input.document_front_image,
      document_back_image: input.document_back_image,
      expiry_date: input.expiry_date,
    };

    const formData = new FormData();

    Object.keys(inputForm).forEach((key) => {
      formData.append(key, inputForm[key]);
    });

    setLoading(true)

    try {
      setLoading(true);

      const res = await axios.post(`${BASE_URL}/kyc/new_kyc_insert`, formData, { headers: getApiHeaders(true) });

      if (res.data.code === 200) {
        if (preview.front) URL.revokeObjectURL(preview.front);
        if (preview.back) URL.revokeObjectURL(preview.back);
        setInput({
          // title: "",
          doctype: "",
          document_front_image: null,
          document_back_image: null,
          expiry_date: ""
        });

        setPreview({
          front: null,
          back: null
        });

        if (frontFileRef.current) {
          frontFileRef.current.value = "";
        }
        if (backFileRef.current) {
          backFileRef.current.value = "";
        }
        if (fileUpload.current) {
          fileUpload.current.value = "";
        }

        MessageToster('success', res.data.message);
        getTabelData();
        getKycDocumentList();
        return true;

      } else {
        MessageToster('error', res.data.message);
        return false;
      }
    } catch (error) {
      handleError(error);
      return false;
    }
    finally {
      setLoading(false);
    }
  };

  const getTabelData = async () => {
    setLoading(true);

    const dataofmid = sessionStorage.getItem('mid');
    // const inputBody = {
    //   mode: "",
    //   mid: Number(dataofmid)
    // };

    try {
      const res = await axios.get(`${BASE_URL}/kyc/new_kyc_get_db_data`, { headers: getApiHeaders() });

      // if (res.data.code === 200) {
      //   closeModal();
      //   setKycData(res.data.message || [])
      // } else {
      //   MessageToster('error', res.data.message);
      // }
      if (res.data.code === 200) {
        closeModal();
        let docs = res.data.message;

        if (
          Array.isArray(docs) &&
          docs.length === 1 &&
          docs[0]?.msg === "No record found"
        ) {
          docs = [];
        }

        const normalizedDocs = Array.isArray(docs)
          ? docs.map((doc) => ({
            ...doc,
            document_front_image: doc.document_front_image
              ? getFullMediaUrl(doc.document_front_image)
              : "",
            document_back_image: doc.document_back_image
              ? getFullMediaUrl(doc.document_back_image)
              : "",
          }))
          : [];

        setKycData(normalizedDocs);
      } else {
        setKycData([]);
        MessageToster("error", res.data.message);
      }
    } catch (error) {
      handleError(error);

    } finally {
      setLoading(false);
    }

  }

  const checkKycStatus = async () => {
    const mid = sessionStorage.getItem("mid");

    setLoading(true);

    // const inputBody = {
    //   mid: Number(mid)
    // };

    try {
      const res = await axios.get(`${BASE_URL}/kyc/kyc_approve_or_reject_status`, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        const status = res.data.data?.status || 'Pending';
        setKycStatus(status);
        // setKycStatus("Approve");
      } else {
        setKycStatus('Pending');
        MessageToster('error', res.data.message || "Failed to fetch KYC status.");
      }
    } catch (error) {
      // handleError(error);
      console.error("Error fetching KYC status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getKycDocumentList = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/kyc/kyc_documents_list`, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        setDocdata(res.data.data)
      } else {
        MessageToster('error', res.data.message);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    checkKycStatus()
    getKycDocumentList()
    getTabelData()

  }, [])


  const isApprove = kycStatus === "Approved";
  const isReject = kycStatus === "Reject";
  const isPending = kycStatus === "Pending";


  useEffect(() => {
    const handleResize = () => {
      const modal = document.querySelector(".document_modal");
      if (!modal) return;

      if (window.innerWidth < 1200) {
        modal.classList.remove("modal-lg");
        modal.classList.add("modal-md");
      } else {
        modal.classList.remove("modal-md");
        modal.classList.add("modal-lg");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <>
      <div id="layout-wrapper">
        {loading &&
          <Loader />
        }
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* start page title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center gap-2 justify-content-between flex-wrap">
                    <h2 className="fw-700 mb-sm-0">Kyc Documents</h2>
                    <button type="button" className="btn btn-primary waves-effect waves-light"
                      data-bs-toggle="modal" data-bs-target=".bs-example-modal-center"
                      onClick={() => getKycDocumentList()}
                    >
                      Upload
                    </button>
                  </div>
                </div>
              </div>
              {/* end page title */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <CommonTable
                        columns={columns}
                        data={kycData}
                        loading={loading}
                        loadingMessage="Loading documents..."
                        noDataMessage="No KYC documents found"
                      />

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            {/* modal */}
            <div
              className="modal fade bs-example-modal-center"
              tabIndex={-1}
              role="dialog"
              aria-hidden="true"
              data-bs-backdrop="static"
              data-bs-keyboard="false"

            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title"> New KYC Document</h5>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      onClick={handleReset}
                      aria-label="Close"
                    />
                  </div>
                  <div className="modal-body">
                    <div className="row">
                      {/* <div className="col-12 mb-3">
                        <div>
                          <label className="form-label" id="title">
                            Title <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            name="title"
                            id="title"
                            value={input.title}
                            onChange={handelchange}
                            placeholder="subject"
                          />
                        </div>
                        {validations.title && (
                          <div className="text-danger small mt-1 d-flex align-items-center">
                            <i className="bx bx-info-circle me-1 font-size-14"></i>
                            <span>{validations.title}</span>
                          </div>
                        )}

                      </div> */}
                      <div className="col-12 mb-3">
                        <div>
                          <label className="form-label">
                            Document Type
                            <span className="text-danger">*</span>
                          </label>
                          <select className="form-select"
                            id="doctype"
                            name="doctype"
                            value={input.doctype}
                            onChange={(e) => handelchange(e)}
                          >
                            <option value="" disabled hidden>Select Document Type</option>
                            {Array.isArray(docdata) && docdata.map((i, index) => {
                              const docName = typeof i === "string" ? i : i?.doc || "";
                              const status = typeof i === "object" && i?.status ? i.status : "";
                              const isDisabled = Boolean(status && status.toLowerCase() !== "not_applied" && status !== "Reject");
                              const formattedStatus = status
                                ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                                : "";
                              const statusLabel = isDisabled && formattedStatus ? ` (${formattedStatus})` : "";

                              return (
                                <option key={index} value={docName} disabled={isDisabled}>
                                  {docName}{statusLabel}
                                </option>
                              );
                            })}

                          </select>
                        </div>
                        {validations.doctype && (
                          <div className="text-danger small mt-1 d-flex align-items-center">
                            <i className="bx bx-info-circle me-1 font-size-14"></i>
                            <span>{validations.doctype}</span>
                          </div>
                        )}

                      </div>
                      <div className="col-12 mb-3">
                        <label className="form-label">Attachments</label>

                        <div className="row">
                          <div className="col-xl-6 col-12">
                            <label className="form-label">Front Side Image</label>

                            <input
                              id="document_front_image"
                              name="document_front_image"
                              type="file"
                              className="form-control"
                              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,image/jpg,application/pdf"
                              onChange={handleImagePreview}
                              // ref={fileUpload}
                              ref={frontFileRef}
                            />

                            {preview.front && (
                              <div className="mt-2">
                                {isPdfFile(input.document_front_image) ? (
                                  <div
                                    className="position-relative d-inline-flex align-items-center gap-2 p-2 bg-light border rounded"
                                    style={{ maxWidth: "240px" }}
                                  >
                                    <a
                                      href={preview.front}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="d-flex align-items-center gap-2 text-decoration-none text-dark overflow-hidden"
                                      title="Click to view PDF"
                                    >
                                      <i className="bx bxs-file-pdf font-size-28 text-danger flex-shrink-0" />
                                      <div className="overflow-hidden text-start">
                                        <span
                                          className="fw-semibold font-size-12 d-block text-truncate"
                                          style={{ maxWidth: "150px" }}
                                          title={input.document_front_image?.name}
                                        >
                                          {input.document_front_image?.name || "Document.pdf"}
                                        </span>
                                        <small className="text-muted font-size-11">
                                          {input.document_front_image?.size
                                            ? `${(input.document_front_image.size / (1024 * 1024)).toFixed(2)} MB`
                                            : "PDF File"}
                                        </small>
                                      </div>
                                    </a>
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm rounded-circle p-0 position-absolute"
                                      style={{
                                        top: "-8px",
                                        right: "-8px",
                                        width: "22px",
                                        height: "22px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                                        border: "2px solid #fff",
                                        cursor: "pointer",
                                        zIndex: 5
                                      }}
                                      onClick={() => handleRemoveFile("document_front_image")}
                                      title="Cancel / Remove"
                                    >
                                      <i className="bx bx-x font-size-16" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="position-relative d-inline-block">
                                    <img
                                      src={preview.front}
                                      alt="Front Preview"
                                      className="rounded border"
                                      style={{ width: "100px", height: "70px", objectFit: "cover", display: "block" }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm rounded-circle p-0 position-absolute"
                                      style={{
                                        top: "-8px",
                                        right: "-8px",
                                        width: "22px",
                                        height: "22px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                                        border: "2px solid #fff",
                                        cursor: "pointer",
                                        zIndex: 5
                                      }}
                                      onClick={() => handleRemoveFile("document_front_image")}
                                      title="Cancel / Remove"
                                    >
                                      <i className="bx bx-x font-size-16" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {validations.document_front_image && (
                              <div className="text-danger small mt-1 d-flex align-items-center">
                                <i className="bx bx-info-circle me-1 font-size-14"></i>
                                <span>{validations.document_front_image}</span>
                              </div>
                            )}
                          </div>

                          <div className="col-xl-6 col-12">
                            <label className="form-label">Back Side Image</label>

                            <input
                              id="document_back_image"
                              name="document_back_image"
                              type="file"
                              className="form-control"
                              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,image/jpg,application/pdf"
                              onChange={handleImagePreview}
                              // ref={fileUpload}
                              ref={backFileRef}
                            />

                            {preview.back && (
                              <div className="mt-2">
                                {isPdfFile(input.document_back_image) ? (
                                  <div
                                    className="position-relative d-inline-flex align-items-center gap-2 p-2 bg-light border rounded"
                                    style={{ maxWidth: "240px" }}
                                  >
                                    <a
                                      href={preview.back}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="d-flex align-items-center gap-2 text-decoration-none text-dark overflow-hidden"
                                      title="Click to view PDF"
                                    >
                                      <i className="bx bxs-file-pdf font-size-28 text-danger flex-shrink-0" />
                                      <div className="overflow-hidden text-start">
                                        <span
                                          className="fw-semibold font-size-12 d-block text-truncate"
                                          style={{ maxWidth: "150px" }}
                                          title={input.document_back_image?.name}
                                        >
                                          {input.document_back_image?.name || "Document.pdf"}
                                        </span>
                                        <small className="text-muted font-size-11">
                                          {input.document_back_image?.size
                                            ? `${(input.document_back_image.size / (1024 * 1024)).toFixed(2)} MB`
                                            : "PDF File"}
                                        </small>
                                      </div>
                                    </a>
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm rounded-circle p-0 position-absolute"
                                      style={{
                                        top: "-8px",
                                        right: "-8px",
                                        width: "22px",
                                        height: "22px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                                        border: "2px solid #fff",
                                        cursor: "pointer",
                                        zIndex: 5
                                      }}
                                      onClick={() => handleRemoveFile("document_back_image")}
                                      title="Cancel / Remove"
                                    >
                                      <i className="bx bx-x font-size-16" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="position-relative d-inline-block">
                                    <img
                                      src={preview.back}
                                      alt="Back Preview"
                                      className="rounded border"
                                      style={{ width: "100px", height: "70px", objectFit: "cover", display: "block" }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm rounded-circle p-0 position-absolute"
                                      style={{
                                        top: "-8px",
                                        right: "-8px",
                                        width: "22px",
                                        height: "22px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                                        border: "2px solid #fff",
                                        cursor: "pointer",
                                        zIndex: 5
                                      }}
                                      onClick={() => handleRemoveFile("document_back_image")}
                                      title="Cancel / Remove"
                                    >
                                      <i className="bx bx-x font-size-16" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {validations.document_back_image && (
                              <div className="text-danger small mt-1 d-flex align-items-center">
                                <i className="bx bx-info-circle me-1 font-size-14"></i>
                                <span>{validations.document_back_image}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <p className="mt-2 text-primary mb-0">
                          Supported: jpeg, jpg, png, pdf — Max size: 5MB
                        </p>
                      </div>

                      <div className="col-12 mb-3">
                        <div>
                          <label className="form-label" id="expiry_date">
                            Expiry Date{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            type="date"
                            id="expiry_date"
                            name="expiry_date"
                            min={getTomorrowDateString()}
                            value={input.expiry_date}
                            onChange={handelchange}
                          />
                        </div>
                        {validations.expiry_date && (
                          <div className="text-danger small mt-1 d-flex align-items-center">
                            <i className="bx bx-info-circle me-1 font-size-14"></i>
                            <span>{validations.expiry_date}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onSubmitForm}
                    >
                      Submit
                    </button>
                  </div>
                </div>
                {/* /.modal-content */}
              </div>
              {/* /.modal-dialog */}
            </div>
            {/* /.modal */}
            {/* model 2 */}
            <div
              className="modal fade doc_modal"
              id="ibAccountModal"
              tabIndex={-1}
              aria-labelledby="ibAccountModalLabel"
              aria-hidden="true"
            >
              <div className="modal-dialog modal-dialog-scrollable modal-lg document_modal mt-5 mx-auto">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="ibAccountModalLabel">Document View</h5>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    />
                  </div>
                  <div className="modal-body">
                    {selectedItem && typeof selectedItem === 'object' && (
                      <div className="row">
                        {/* Front Image */}
                        {selectedItem.front && (
                          <div className={`col-12 ${selectedItem.back ? 'col-xl-6' : ''} mb-3`}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="text-primary mb-0">Front Side Image</h6>
                              {/* Download Button for Front Image */}
                              <button
                                // href={selectedItem.front}
                                // download
                                // target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-success"
                                title="Download Front"
                                // onClick={() => FileSaver(selectedItem.front)}
                                onClick={() => FileSaver(selectedItem.front.replace(
                                  "http://127.0.0.1:1234",
                                  "https://crmapi.blockcryp.com"
                                ))}

                              >
                                <i className="mdi mdi-download-outline me-1" /> Download

                              </button>
                            </div>
                            <div className="image-viewer-box border p-1 rounded">
                              {isPdfUrl(selectedItem.front) ? (
                                <iframe
                                  src={selectedItem.front}
                                  title="Front Document PDF"
                                  style={{ width: "100%", height: "450px", border: "none" }}
                                />
                              ) : (
                                <img
                                  src={selectedItem.front}
                                  alt="Front Document"
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {/* Back Image (Optional) */}
                        {selectedItem.back && (
                          <div className="col-12 col-xl-6 mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="text-primary mb-0">Back Side Image</h6>
                              <button
                                // href={selectedItem.back}
                                // download
                                // target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-success"
                                title="Download Back"
                                onClick={() => FileSaver(selectedItem.back.replace(
                                  "http://127.0.0.1:1234",
                                  "https://crmapi.blockcryp.com"
                                ))}
                              >
                                <i className="mdi mdi-download-outline me-1" /> Download
                              </button>
                            </div>
                            <div className="image-viewer-box border p-1 rounded">
                              {isPdfUrl(selectedItem.back) ? (
                                <iframe
                                  src={selectedItem.back}
                                  title="Back Document PDF"
                                  style={{ width: "100%", height: "450px", border: "none" }}
                                />
                              ) : (
                                <img
                                  src={selectedItem.back}
                                  alt="Back Document"
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {!selectedItem.front && !selectedItem.back && (
                          <div className="col-12 text-center">
                            <p>No document images available for viewing.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedItem && typeof selectedItem === 'string' && (
                      <div className="text-center">
                        <a
                          href={selectedItem}
                          download
                          // target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-success mb-3"
                          title="Download Document"
                        >
                          <i className="mdi mdi-download-outline me-1" /> Download
                        </a>
                        {isPdfUrl(selectedItem) ? (
                          <iframe
                            src={selectedItem}
                            title="Document PDF"
                            style={{ width: "100%", height: "500px", border: "none" }}
                          />
                        ) : (
                          <img src={selectedItem} alt="Document" className="img-fluid w-100 h-100" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* model 2 */}
          </div>
          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}
